import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { COLOR_PRIMARY, CALCULATOR_API_URL, exchangeRates } from 'src/utils/const';
import { formatCurrency, formatDisplayCurrency, deepCopyObject } from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import { convertApiCurrency } from '../../../helper';
import { inputSections } from '../../../configs/FinancialInputConfig';
import { MetricsState, ReportTypes } from '../../../propTypes';
import { InputTextField } from 'src/App/Dashboards/Market/Components/Inputs';
import { CalculatorGraphSection, CalculatorKPICard, CalculatorKPIToggleModal } from './';
import ToggleVisibilityButton from 'src/App/Dashboards/Market/Components/Buttons/ToggleVisibilityButton';
import SubsectionHeading from '../../../UtilityComponents/SubsectionHeading';
import { calculateDerivedKPIs, operatingDaysFactor } from '../../../utils/calculateDerivedKPIs';
import InvestmentMetricsHighlightBox, {
  BUY_MANAGE_CAP_LAYOUT_KEYS,
  FORECAST_OPEX_ANCHOR_ID,
  FORECAST_LOAN_ANCHOR_ID,
  FORECAST_STARTUP_ANCHOR_ID,
  RENT_PROFIT_LAYOUT_KEYS,
  BreakdownResultChevron,
  OperatingExpensesAdjustLink,
  LoanSetupLink,
  StartupCostSetupLink,
  formatPercentMetric,
  getTooltipForMetricKey,
  stripLeadingMinus,
} from './InvestmentMetricsHighlightBox';

const REPORT_TYPES: { label: string; value: ReportTypes }[] = [
  { label: 'Buying Property', value: 'buy' },
  { label: 'Rental Arbitrage', value: 'rent' },
  { label: 'Property Manager', value: 'manage' },
];

/** Red nudge so users know they’re filling purchase price / rent, not editing the headline KPI. */
const FORECAST_INLINE_INPUT_NUDGE_SX = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#b91c1c',
  mb: 1,
  lineHeight: 1.45,
} as const;

const InputSection = ({
  sectionKey,
  sectionConfig,
  stateObj,
  handleValueChange,
  reportType,
  currency,
  baseRevenue,
}: {
  sectionKey: string;
  sectionConfig: any;
  stateObj: any;
  handleValueChange: (key: string, value: any) => void;
  reportType: ReportTypes;
  currency: string;
  baseRevenue: number;
}) => {
  const isSectionAllowed = sectionConfig.allowedModes.includes(reportType);
  if (!isSectionAllowed) return null;

  const [strReady, setStrReady] = useState(false);

  const isToggleInverted = sectionConfig.toggleInverted;
  const isSectionDisabled = isToggleInverted
    ? strReady
    : sectionConfig.canToggle && !(sectionConfig.toggleKey && stateObj[sectionConfig.toggleKey]);

  const sectionsWithTotals: Record<string, { suffix: string; useKey?: string }> = {
    operatingExpenses: { suffix: '/year' },
    taxes: { suffix: '/year' },
    startupCost: { suffix: '' },
    debt: { suffix: '/year', useKey: 'mortgage_annual' },
  };

  const totalConfig = sectionsWithTotals[sectionKey];
  let totalDisplay: string | null = null;
  if (totalConfig && !isSectionDisabled) {
    const currencySymbol = exchangeRates[currency]?.symbol || '$';
    if (totalConfig.useKey) {
      const val = stateObj[totalConfig.useKey];
      totalDisplay = val ? `${currencySymbol}${formatCurrency(String(Math.round(val)))}${totalConfig.suffix}` : null;
    } else {
      let sum = Object.keys(sectionConfig.fields).reduce((acc: number, key: string) => {
        const field = sectionConfig.fields[key];
        if (field.unit === '$') {
          return acc + (parseFloat(stateObj[key]) || 0);
        }
        return acc;
      }, 0);
      if (sectionKey === 'operatingExpenses' && baseRevenue > 0) {
        sum += (Math.min(parseFloat(stateObj.management_fee) || 0, 40) / 100) * baseRevenue;
      }
      if (sectionKey === 'operatingExpenses') {
        sum = Math.round(sum * operatingDaysFactor(stateObj.owner_occupied_days));
      }
      totalDisplay = sum > 0 ? `${currencySymbol}${formatCurrency(String(Math.round(sum)))}${totalConfig.suffix}` : null;
    }
  }

  const startupFieldKeys = ['remodelling', 'furnishing_cost', 'closing_cost'];
  const secondaryGroup = sectionConfig.secondaryGroup;
  const secondaryFieldKeys = secondaryGroup?.fieldKeys || [];
  const allFields = Object.entries(sectionConfig.fields);
  const excludedFromPrimary = isToggleInverted ? startupFieldKeys : secondaryFieldKeys;
  const primaryFields = excludedFromPrimary.length > 0
    ? allFields.filter(([key]) => !excludedFromPrimary.includes(key))
    : allFields;
  const startupFields = isToggleInverted ? allFields.filter(([key]) => startupFieldKeys.includes(key)) : [];
  const secondaryFields = secondaryGroup ? allFields.filter(([key]) => secondaryFieldKeys.includes(key)) : [];

  const renderField = ([key, field]: [string, any], disabled: boolean) => {
    if (field.isSlider) {
      const min = field.sliderMin ?? 0;
      const max = field.sliderMax ?? 100;
      const raw = parseFloat(stateObj[key]) || 0;
      const value = raw > max ? max : raw;
      return (
        <Grid item xs={12} key={key}>
          <Box sx={{ px: '4px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '2px' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: disabled ? '#9ca3af' : '#374151' }}>
                {field.label}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: disabled ? '#9ca3af' : '#037A90' }}>
                {value}%
              </Typography>
            </Box>
            <Slider
              size="small"
              min={min}
              max={max}
              step={1}
              value={value}
              onChange={(_, v) => handleValueChange(key, v as number)}
              disabled={disabled}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${v}%`}
              sx={{
                color: '#037A90',
                '& .MuiSlider-thumb': { width: 14, height: 14 },
                '& .MuiSlider-valueLabel': { fontSize: '11px' },
              }}
            />
          </Box>
        </Grid>
      );
    }
    if (field.isDropdown) {
      return (
        <Grid item xs={6} key={key}>
          <Autocomplete
            disablePortal
            size="small"
            options={field.dropdownOptions || []}
            renderInput={params => <TextField {...params} label={field.label} size="small" />}
            value={stateObj[key] || ''}
            onChange={(_, value) => handleValueChange(key, value)}
            disabled={disabled}
          />
        </Grid>
      );
    }
    return (
      <Grid item xs={6} key={key}>
        <InputTextField
          field={{ ...field, key }}
          stateObj={stateObj}
          handleValueChange={handleValueChange}
          currency={currency}
          disabled={disabled}
        />
      </Grid>
    );
  };

  return (
    <Box sx={{
      border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px',
      background: '#fff',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
        <Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
            {(isToggleInverted || secondaryGroup) ? sectionConfig.displayName.split(' & ')[0] : sectionConfig.displayName}
          </Typography>
          {totalDisplay && (
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#037A90', mt: '2px' }}>
              {totalDisplay}
            </Typography>
          )}
        </Box>
        {sectionConfig.canToggle && !isToggleInverted && (
          <Switch
            size="small"
            checked={sectionConfig.toggleKey ? stateObj[sectionConfig.toggleKey] || false : false}
            onChange={(_, checked) => handleValueChange(sectionConfig.toggleKey || '', checked)}
          />
        )}
      </Box>
      <Grid container spacing={1.5}>
        {primaryFields.map(entry => renderField(entry, isToggleInverted ? false : isSectionDisabled))}
      </Grid>

      {isToggleInverted && startupFields.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '16px', mb: '10px', pt: '12px', borderTop: '1px solid #f3f4f6' }}>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                Startup Cost
              </Typography>
              {!strReady && (() => {
                const sym = exchangeRates[currency]?.symbol || '$';
                const sum = startupFieldKeys.reduce((acc, k) => acc + (parseFloat(stateObj[k]) || 0), 0);
                return sum > 0 ? (
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#037A90', mt: '1px' }}>
                    {sym}{formatCurrency(String(Math.round(sum)))}
                  </Typography>
                ) : null;
              })()}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography sx={{ fontSize: '11px', color: strReady ? '#037A90' : '#6b7280' }}>
                STR ready
              </Typography>
              <Switch
                size="small"
                checked={strReady}
                onChange={(_, checked) => {
                  setStrReady(checked);
                  if (checked) {
                    startupFieldKeys.forEach(k => handleValueChange(k, 0));
                  }
                }}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#037A90' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#037A90' } }}
              />
            </Box>
          </Box>
          {!strReady && (
            <Grid container spacing={1.5}>
              {startupFields.map(entry => renderField(entry, false))}
            </Grid>
          )}
        </>
      )}

      {secondaryGroup && secondaryFields.length > 0 && (
        <>
          <Box sx={{ mt: '16px', mb: '10px', pt: '12px', borderTop: '1px solid #f3f4f6' }}>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                {secondaryGroup.title}
              </Typography>
              {(() => {
                const sym = exchangeRates[currency]?.symbol || '$';
                const sum = secondaryFieldKeys.reduce((acc: number, k: string) => acc + (parseFloat(stateObj[k]) || 0), 0);
                return sum > 0 ? (
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#037A90', mt: '1px' }}>
                    {sym}{formatCurrency(String(Math.round(sum)))}{secondaryGroup.suffix || ''}
                  </Typography>
                ) : null;
              })()}
            </Box>
          </Box>
          <Grid container spacing={1.5}>
            {secondaryFields.map(entry => renderField(entry, false))}
          </Grid>
        </>
      )}
    </Box>
  );
};

const scaleValue = (val: any, unit: string, multiplier: number) => {
  if (val == null || val === 'loading') return val;
  if (unit !== '$') return val;
  if (typeof val === 'number') return val * multiplier;
  return val;
};

const OutputSubsection = ({
  title,
  metrics,
  reportKPIData,
  currency,
  isUserReportOwner,
  hanldeKpiEdit,
  selectedCompsCount,
  ltmDateRange,
  timePeriod = 1,
}: {
  title: string;
  metrics: MetricsState;
  reportKPIData: any;
  currency: string;
  isUserReportOwner: boolean;
  hanldeKpiEdit: (key: string, value: any) => void;
  selectedCompsCount: number;
  ltmDateRange: string;
  timePeriod?: number;
}) => {
  const visibleKpis = Object.values(metrics).filter(kpi => kpi.visibility);
  if (visibleKpis.length === 0) return null;

  return (
    <Box sx={{ mb: '16px' }}>
      <SubsectionHeading title={title} />
      <Grid container direction="row" alignItems="flex-start" justifyContent="flex-start" spacing={1}>
        {visibleKpis.map(kpi => (
          <CalculatorKPICard
            key={kpi.config.key}
            kpiKey={kpi.config.key}
            kpiName={kpi.config.displayName}
            value={scaleValue(reportKPIData[kpi.config.key], kpi.config.unit, timePeriod)}
            valuePercentiles={{
              25: scaleValue(reportKPIData[25]?.[kpi.config.key], kpi.config.unit, timePeriod),
              50: scaleValue(reportKPIData[50]?.[kpi.config.key], kpi.config.unit, timePeriod),
              75: scaleValue(reportKPIData[75]?.[kpi.config.key], kpi.config.unit, timePeriod),
              90: scaleValue(reportKPIData[90]?.[kpi.config.key], kpi.config.unit, timePeriod),
            }}
            tooltipConfig={kpi.config.tooltipConfig}
            unitType={kpi.config.unit}
            currency={currency}
            isUserReportOwner={isUserReportOwner}
            isEditable={false}
            handleEditModalOpen={() => {}}
            editMode={null}
            hanldeKpiEdit={hanldeKpiEdit}
            selectedCompsCount={selectedCompsCount}
            ltmDateRange={ltmDateRange}
          />
        ))}
      </Grid>
    </Box>
  );
};

const TIME_PERIODS = [
  { label: '1 Month', value: 1 / 12 },
  { label: '1 Year', value: 1 },
];

/** Keep hero inline price/rent fields visible for this long after the last edit (timer resets on each change). */
const INLINE_FORECAST_FIELD_HOLD_MS = 5000;

const InlineCalculatorForm = ({
  reportType,
  onReportTypeChange,
  inputEditState,
  handleInputEdit,
  hanldeOnInputApply,
  hanldeKpiEdit,
  kpiData,
  currency,
  metricsState,
  isUserReportOwner,
  handleLoginOrPricingModalOpen,
  isUserSubscribed,
  selectedCompsCount,
  ltmDateRange,
  performanceRef,
  subsections,
  toggleVisibility,
  trackEditOpened,
  reportId,
}: {
  reportType: ReportTypes;
  onReportTypeChange: (type: ReportTypes) => void;
  inputEditState: Record<string, any>;
  handleInputEdit: (key: string, value: any) => void;
  hanldeOnInputApply: () => void;
  hanldeKpiEdit: (key: string, value: any) => void;
  kpiData: any;
  currency: string;
  metricsState: { [key: string]: MetricsState };
  isUserReportOwner: boolean;
  handleLoginOrPricingModalOpen: any;
  isUserSubscribed: boolean;
  selectedCompsCount: number;
  ltmDateRange: string;
  performanceRef?: any;
  subsections: { title: string; key: string }[];
  toggleVisibility?: any;
  trackEditOpened?: any;
  reportId?: string;
}) => {
  const [toggleListOpen, setToggleListOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState(reportType === 'rent' ? 1 / 12 : 1);
  const [activeMetricTab, setActiveMetricTab] = useState<'cap_rate' | 'coc_return' | 'ten_year_return'>('cap_rate');
  const [holdCapPriceInput, setHoldCapPriceInput] = useState(false);
  const [holdRentInput, setHoldRentInput] = useState(false);
  /** 10-yr tab: hide full table+chart while cash invested is $0 until user clicks Calculate (or enters investment). */
  const [tenYearShowFullBreakdown, setTenYearShowFullBreakdown] = useState(false);
  const prevTotalCashInvestedRef = useRef<number | null>(null);
  const capPriceHoldTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rentHoldTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setTimePeriod(reportType === 'rent' ? 1 / 12 : 1);
  }, [reportType]);

  useEffect(() => {
    setHoldCapPriceInput(false);
    setHoldRentInput(false);
    if (capPriceHoldTimeoutRef.current) {
      clearTimeout(capPriceHoldTimeoutRef.current);
      capPriceHoldTimeoutRef.current = null;
    }
    if (rentHoldTimeoutRef.current) {
      clearTimeout(rentHoldTimeoutRef.current);
      rentHoldTimeoutRef.current = null;
    }
  }, [reportType]);

  useEffect(() => {
    return () => {
      if (capPriceHoldTimeoutRef.current) clearTimeout(capPriceHoldTimeoutRef.current);
      if (rentHoldTimeoutRef.current) clearTimeout(rentHoldTimeoutRef.current);
    };
  }, []);

  const bumpCapPriceHold = () => {
    setHoldCapPriceInput(true);
    if (capPriceHoldTimeoutRef.current) clearTimeout(capPriceHoldTimeoutRef.current);
    capPriceHoldTimeoutRef.current = setTimeout(() => {
      setHoldCapPriceInput(false);
      capPriceHoldTimeoutRef.current = null;
    }, INLINE_FORECAST_FIELD_HOLD_MS);
  };

  const bumpRentHold = () => {
    setHoldRentInput(true);
    if (rentHoldTimeoutRef.current) clearTimeout(rentHoldTimeoutRef.current);
    rentHoldTimeoutRef.current = setTimeout(() => {
      setHoldRentInput(false);
      rentHoldTimeoutRef.current = null;
    }, INLINE_FORECAST_FIELD_HOLD_MS);
  };

  const baseRevenue = kpiData?.ltm_revenue || 0;
  const derivedKPIs = useMemo(
    () => calculateDerivedKPIs(inputEditState, baseRevenue),
    [inputEditState, baseRevenue]
  );
  const effectiveKpiData = useMemo(
    () => (kpiData ? { ...kpiData, ...derivedKPIs } : kpiData),
    [kpiData, derivedKPIs]
  );

  useEffect(() => {
    const raw = effectiveKpiData?.total_cash_invested;
    const tci = typeof raw === 'number' && !Number.isNaN(raw) ? raw : 0;
    if (tci > 0) {
      setTenYearShowFullBreakdown(true);
    } else if (
      prevTotalCashInvestedRef.current != null &&
      prevTotalCashInvestedRef.current > 0 &&
      tci <= 0
    ) {
      setTenYearShowFullBreakdown(false);
    }
    prevTotalCashInvestedRef.current = tci;
  }, [effectiveKpiData?.total_cash_invested]);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const lastSavedSnapshot = useRef<string>(JSON.stringify(inputEditState));

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const currentSnapshot = JSON.stringify(inputEditState);
    if (currentSnapshot === lastSavedSnapshot.current) return;

    setSaveStatus('idle');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const body = {
          update_type: 'calculator',
          id: reportId,
          report_type: reportType,
          ...inputEditState,
          is_loan_taken: inputEditState.is_loan_taken ? 1 : 0,
          wanna_sell: inputEditState.wanna_sell ? 1 : 0,
        };
        const convertedBody = convertApiCurrency(deepCopyObject(body), currency, true, true);
        await callApi(
          CALCULATOR_API_URL,
          { body: { ...convertedBody, ota: 'airbnb' }, queryParams: { apiType: 'v3' } },
          true, false, '', 'PUT', true, undefined, true, true
        );
        lastSavedSnapshot.current = currentSnapshot;
        setSaveStatus('saved');
      } catch (e) {
        console.error('Silent auto-save failed:', e);
        setSaveStatus('idle');
      }
    }, 3000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputEditState]);

  const groupedMetrics = subsections.map(s => ({
    subsection: s.title,
    subsectionKey: s.key,
    metrics: Object.entries(metricsState[s.key] || {}).map(([key, kpi]) => ({
      name: kpi.config.displayName,
      isVisible: kpi.visibility,
      isAllowed: kpi.allowed,
      key,
    })),
  }));

  if (performanceRef) {
    const groupedMetrics = subsections.map(s => ({
      subsection: s.title,
      subsectionKey: s.key,
      metrics: Object.entries(metricsState[s.key] || {}).map(([key, kpi]) => ({
        name: kpi.config.displayName,
        isVisible: kpi.visibility,
        isAllowed: kpi.allowed,
        key,
      })),
    }));
    performanceRef.current = { metadata: groupedMetrics, data: kpiData, effectiveKpiData };
  }

  return (
    <Box sx={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px',
      padding: '24px', width: '100%',
    }}>
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', mb: '16px' }}>
        Forecast Your Income
      </Typography>

      {/* Report Type Chips */}
      <Box sx={{ display: 'flex', gap: '8px', mb: '12px' }}>
        {REPORT_TYPES.map(rt => (
          <Chip
            key={rt.value}
            label={rt.label}
            variant={reportType === rt.value ? 'filled' : 'outlined'}
            onClick={() => onReportTypeChange(rt.value)}
            sx={{
              fontWeight: 600, fontSize: '13px',
              ...(reportType === rt.value
                ? { background: COLOR_PRIMARY, color: '#fff', '&:hover': { background: '#026b7d' } }
                : { borderColor: '#d1d5db', color: '#4b5563', '&:hover': { background: '#f3f4f6' } }
              ),
            }}
          />
        ))}
      </Box>

      {/* Metric Tab Badges (buy/manage only) — indented with left border rail */}
      {(reportType === 'buy' || reportType === 'manage') && (
        <Box
          sx={{
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap',
            mb: '16px',
            ml: '6px',
            pl: '12px',
            borderLeft: '2px solid #d1d5db',
          }}
        >
          {([
            { key: 'cap_rate' as const, label: 'Cap Rate' },
            { key: 'coc_return' as const, label: 'Cash on Cash Return' },
            { key: 'ten_year_return' as const, label: '10 Year Return Rate' },
          ]).map(tab => (
            <Chip
              key={tab.key}
              label={tab.label}
              size="small"
              variant={activeMetricTab === tab.key ? 'filled' : 'outlined'}
              onClick={() => setActiveMetricTab(tab.key)}
              sx={{
                fontWeight: 600, fontSize: '11px', height: '26px',
                ...(activeMetricTab === tab.key
                  ? { background: COLOR_PRIMARY, color: '#fff', '&:hover': { background: '#026b7d' } }
                  : { borderColor: '#d1d5db', color: '#6b7280', '&:hover': { background: '#f3f4f6' } }
                ),
              }}
            />
          ))}
        </Box>
      )}

      {/* All Metrics toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '8px' }}>
        <ToggleVisibilityButton
          title="All Metrics"
          isDisabled={!isUserReportOwner}
          actionOnClick={() => {
            setToggleListOpen(true);
            trackEditOpened?.('kpi');
          }}
          isLocked={false}
        />
      </Box>

      {/* Tabbed highlight content + period */}
      {effectiveKpiData && (() => {
        const sym = exchangeRates[currency]?.symbol || '$';
        const tp = timePeriod;
        const fmt = (val: number | null | undefined, unit: string = '$') => {
          if (val == null) return '--';
          if (unit === '%') return formatPercentMetric(val);
          return `${sym}${formatCurrency(String(Math.round(val * tp)))}`;
        };
        const fmtPrice = (val: number | null | undefined) => {
          if (val == null || val === undefined) return '--';
          return formatDisplayCurrency(currency, val);
        };
        const periodLabel = tp < 1 ? '/mo' : tp === 1 ? '/yr' : `/${tp}yr`;

        const allVisibleMetrics: { key: string; label: string; unit: string; tooltip: string | null; value: any }[] = [];
        subsections.forEach(s => {
          const sectionMetrics = metricsState[s.key];
          if (!sectionMetrics) return;
          Object.values(sectionMetrics).forEach((kpi: any) => {
            if (!kpi.visibility || !kpi.allowed) return;
            const tooltipLines = kpi.config.tooltipConfig?.definition || [];
            const formula = kpi.config.tooltipConfig?.formula;
            const tooltipText = [formula, ...tooltipLines].filter(Boolean).join(' — ');
            allVisibleMetrics.push({
              key: kpi.config.key,
              label: kpi.config.displayName,
              unit: kpi.config.unit,
              tooltip: tooltipText || null,
              value: effectiveKpiData[kpi.config.key],
            });
          });
        });

        const profitKeys = ['yearly_net_profit_before_tax', 'net_operating_income'];
        const isBuyOrManage = reportType === 'buy' || reportType === 'manage';
        const propertyPriceRaw = inputEditState.property_price;
        const hasPurchasePrice =
          propertyPriceRaw != null && propertyPriceRaw !== '' && Number(propertyPriceRaw) > 0;
        const capTooltip = getTooltipForMetricKey(metricsState, 'cap_rate');
        const profitTooltip = getTooltipForMetricKey(metricsState, 'yearly_net_profit_before_tax');
        const noiTooltip = getTooltipForMetricKey(metricsState, 'net_operating_income');
        const opexTooltip = getTooltipForMetricKey(metricsState, 'annual_operating_expenses_total');
        const cocTooltip = getTooltipForMetricKey(metricsState, 'cash_on_cash_return');
        const mortgageTooltip = getTooltipForMetricKey(metricsState, 'mortgage_annual');
        const cashInvestedTooltip = getTooltipForMetricKey(metricsState, 'total_cash_invested');
        const annualReturnTooltip = getTooltipForMetricKey(metricsState, 'annual_return_rate');

        const revenueVal = effectiveKpiData.ltm_revenue;
        const reportLtmRevenueAnnual = Number(kpiData?.ltm_revenue) || 0;
        const ownerOccupiedRevenueLossAnnual = Math.max(
          0,
          reportLtmRevenueAnnual -
            Math.round(reportLtmRevenueAnnual * operatingDaysFactor(inputEditState.owner_occupied_days))
        );
        const revenueOwnerOccupiedDetail =
          ownerOccupiedRevenueLossAnnual > 0
            ? `−${stripLeadingMinus(fmt(ownerOccupiedRevenueLossAnnual, '$'))} from owner-occupied days`
            : undefined;
        const opexVal = effectiveKpiData.annual_operating_expenses_total;
        const noiVal = effectiveKpiData.net_operating_income;
        const capVal = effectiveKpiData.cap_rate;
        const profitVal = effectiveKpiData.yearly_net_profit_before_tax;
        const cocVal = effectiveKpiData.cash_on_cash_return;
        const mortgageVal = effectiveKpiData.mortgage_annual;
        const cashInvestedVal = effectiveKpiData.total_cash_invested;
        const hasCashInvested = cashInvestedVal != null && cashInvestedVal > 0;
        const numInp = (v: unknown) => {
          const x = parseFloat(String(v ?? ''));
          return Number.isFinite(x) ? x : 0;
        };
        const downPaymentZero = numInp(inputEditState.down_payment) === 0;
        const startupAllZero =
          numInp(inputEditState.remodelling) +
            numInp(inputEditState.furnishing_cost) +
            numInp(inputEditState.closing_cost) ===
          0;
        const showTotalCashInvestedNudge =
          !hasCashInvested && downPaymentZero && startupAllZero;

        const periodSelector = (
          <Box sx={{ display: 'flex', gap: '6px', mb: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#4b5563', mr: '4px' }}>
              Period:
            </Typography>
            {TIME_PERIODS.map(tpOption => (
              <Chip
                key={tpOption.value}
                label={tpOption.label}
                size="small"
                variant={timePeriod === tpOption.value ? 'filled' : 'outlined'}
                onClick={() => setTimePeriod(tpOption.value)}
                sx={{
                  fontWeight: 600, fontSize: '12px',
                  ...(timePeriod === tpOption.value
                    ? { background: '#037A90', color: '#fff', '&:hover': { background: '#026b7d' } }
                    : { borderColor: '#d1d5db', color: '#4b5563', '&:hover': { background: '#f3f4f6' } }
                  ),
                }}
              />
            ))}
          </Box>
        );

        if (isBuyOrManage) {
          const extraMetrics = allVisibleMetrics.filter(m => !BUY_MANAGE_CAP_LAYOUT_KEYS.has(m.key));

          const capRateContent = (
            <InvestmentMetricsHighlightBox
              id="investment-metrics-highlight-forecast"
              heroes={[
                {
                  key: 'cap-rate',
                  label: 'Cap rate',
                  tooltip: capTooltip,
                  emphasis: 'primary',
                  valueVariant: 'hero',
                  value: (
                    <Box sx={{ mt: 0.5 }}>
                      {hasPurchasePrice && (
                        <Typography
                          sx={{
                            fontSize: { xs: '28px', sm: '32px' },
                            fontWeight: 800,
                            color: COLOR_PRIMARY,
                            lineHeight: 1.1,
                            mb: 1,
                          }}
                        >
                          {formatPercentMetric(capVal)}
                        </Typography>
                      )}
                      {!hasPurchasePrice && (
                        <Typography sx={FORECAST_INLINE_INPUT_NUDGE_SX}>
                          Enter your purchase price below
                        </Typography>
                      )}
                      <TextField
                        size="small"
                        placeholder="Purchase price"
                        type="tel"
                        value={
                          inputEditState.property_price === '' ||
                          inputEditState.property_price === undefined ||
                          inputEditState.property_price === null
                            ? ''
                            : formatCurrency(String(inputEditState.property_price))
                        }
                        onChange={e => {
                          const parsed = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                          handleInputEdit('property_price', parsed);
                          bumpCapPriceHold();
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {exchangeRates[currency]?.symbol || '$'}
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '200px', '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
                      />
                    </Box>
                  ),
                  footnote:
                    hasPurchasePrice
                      ? 'NOI ÷ purchase price · Unlevered yield (before mortgage payments)'
                      : undefined,
                },
              ]}
              breakdown={{
                title: 'Cap rate breakdown',
                description:
                  'Uses estimated revenue from your report. Operating expenses exclude mortgage and taxes.',
                rows: [
                  {
                    rowKey: 'cap-br-rev',
                    label: (
                      <>
                        Estimated revenue (LTM){' '}
                        <Typography component="span" sx={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>
                          (from report)
                        </Typography>
                      </>
                    ),
                    value: fmt(revenueVal, '$'),
                    valueDetail: revenueOwnerOccupiedDetail,
                    tooltip: getTooltipForMetricKey(metricsState, 'ltm_revenue'),
                  },
                  {
                    rowKey: 'cap-br-opex',
                    label: (<>− Operating expenses <OperatingExpensesAdjustLink /></>),
                    value: opexVal != null ? `− ${stripLeadingMinus(fmt(opexVal, '$'))}` : '—',
                    tooltip: opexTooltip,
                  },
                  {
                    rowKey: 'cap-br-noi',
                    label: (<>= Net operating income (NOI) <BreakdownResultChevron /></>),
                    value: fmt(noiVal, '$'),
                    tooltip: noiTooltip,
                    emphasize: true,
                  },
                  {
                    rowKey: 'cap-br-price',
                    label: 'Purchase price (unlevered)',
                    value: hasPurchasePrice ? fmtPrice(Number(propertyPriceRaw)) : '—',
                  },
                  {
                    rowKey: 'cap-br-rate',
                    label: (<>= Cap rate (NOI ÷ price) <BreakdownResultChevron /></>),
                    value: hasPurchasePrice ? formatPercentMetric(capVal) : '—',
                    tooltip: capTooltip,
                    emphasize: true,
                  },
                ],
              }}
              otherMetrics={{ items: extraMetrics, profitKeys, fmt, periodLabel }}
            />
          );

          const cocReturnContent = (
            <InvestmentMetricsHighlightBox
              id="investment-metrics-highlight-coc"
              heroes={[
                {
                  key: 'coc-return',
                  label: 'Cash on Cash Return',
                  tooltip: cocTooltip,
                  emphasis: 'primary',
                  valueVariant: 'hero',
                  value: hasCashInvested ? (
                    formatPercentMetric(cocVal)
                  ) : (
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#6b7280', mt: 0.5 }}>
                      Add investment details to see CoC return
                    </Typography>
                  ),
                  footnote: 'Net profit before tax ÷ total cash invested',
                },
              ]}
              breakdown={{
                title: 'Cash on cash return breakdown',
                description:
                  'Measures annual cash flow as a percentage of the total cash you invested.',
                rows: [
                  {
                    rowKey: 'coc-br-rev',
                    label: (
                      <>
                        Estimated revenue (LTM){' '}
                        <Typography component="span" sx={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>
                          (from report)
                        </Typography>
                      </>
                    ),
                    value: fmt(revenueVal, '$'),
                    valueDetail: revenueOwnerOccupiedDetail,
                    tooltip: getTooltipForMetricKey(metricsState, 'ltm_revenue'),
                  },
                  {
                    rowKey: 'coc-br-opex',
                    label: (<>− Operating expenses <OperatingExpensesAdjustLink /></>),
                    value: opexVal != null ? `− ${stripLeadingMinus(fmt(opexVal, '$'))}` : '—',
                    tooltip: opexTooltip,
                  },
                  {
                    rowKey: 'coc-br-noi',
                    label: '= Net operating income (NOI)',
                    value: fmt(noiVal, '$'),
                    tooltip: noiTooltip,
                  },
                  {
                    rowKey: 'coc-br-mortgage',
                    label: (<>− Mortgage payments <LoanSetupLink /></>),
                    value: mortgageVal ? `− ${stripLeadingMinus(fmt(mortgageVal, '$'))}` : '—',
                    tooltip: mortgageTooltip,
                  },
                  {
                    rowKey: 'coc-br-profit',
                    label: (<>= Net profit before tax <BreakdownResultChevron /></>),
                    value: fmt(profitVal, '$'),
                    tooltip: profitTooltip,
                    emphasize: true,
                  },
                  {
                    rowKey: 'coc-br-invested',
                    label: (
                      <>
                        Total cash invested (down payment + startup)
                        {showTotalCashInvestedNudge && (
                          <>
                            {' '}
                            <LoanSetupLink />
                            <StartupCostSetupLink />
                          </>
                        )}
                      </>
                    ),
                    value: hasCashInvested ? fmtPrice(cashInvestedVal) : '—',
                    tooltip: cashInvestedTooltip,
                  },
                  {
                    rowKey: 'coc-br-return',
                    label: (<>= CoC Return (profit ÷ invested) <BreakdownResultChevron /></>),
                    value: hasCashInvested ? formatPercentMetric(cocVal) : '—',
                    tooltip: cocTooltip,
                    emphasize: true,
                  },
                ],
              }}
              otherMetrics={{ items: extraMetrics, profitKeys, fmt, periodLabel }}
            />
          );

          const TEN_YR = 10;
          const appreciationPctTen = inputEditState.property_appreciation_rate ?? 4;
          const propertyPriceNum = Number(propertyPriceRaw) || 0;
          const annualProfitTen = Number(profitVal) || 0;
          const tciTen = Number(cashInvestedVal) || 0;
          const cumulativeProfitTenYr = Math.round(annualProfitTen * TEN_YR);
          const salePriceYearTen =
            propertyPriceNum > 0
              ? propertyPriceNum * Math.pow(1 + appreciationPctTen / 100, TEN_YR)
              : 0;
          const totalGainTenYr =
            tciTen > 0 ? Math.round(cumulativeProfitTenYr + salePriceYearTen - tciTen) : null;
          const totalReturnPctTenYr =
            tciTen > 0 && totalGainTenYr != null
              ? Math.round((totalGainTenYr / tciTen) * 10000) / 100
              : null;
          const annualReturnVal = effectiveKpiData.annual_return_rate;
          const showTenYearCalculateGate = !hasCashInvested && !tenYearShowFullBreakdown;

          const tenYearReturnContent = (
            <Box sx={{ mb: '20px' }}>
              {!hasPurchasePrice && (
                <Box
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    p: '14px 16px',
                    background: '#f9fafb',
                    mb: '12px',
                  }}
                >
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', mb: 1 }}>
                    Enter your purchase price to calculate 10-year return
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Purchase price"
                    type="tel"
                    value={
                      inputEditState.property_price === '' ||
                      inputEditState.property_price === undefined ||
                      inputEditState.property_price === null
                        ? ''
                        : formatCurrency(String(inputEditState.property_price))
                    }
                    onChange={e => {
                      const parsed = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                      handleInputEdit('property_price', parsed);
                      bumpCapPriceHold();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {exchangeRates[currency]?.symbol || '$'}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: '200px', '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
                  />
                </Box>
              )}
              {hasPurchasePrice && showTenYearCalculateGate && (
                <Box
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    p: '20px 18px',
                    background: '#f9fafb',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 1 }}>
                    Total cash invested is $0 (defaults)
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#6b7280', mb: 2, lineHeight: 1.5, maxWidth: '420px', mx: 'auto' }}>
                    Add down payment and startup costs below for a real return rate, or click Calculate to open the
                    full breakdown and chart with current inputs.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setTenYearShowFullBreakdown(true)}
                    sx={{
                      background: COLOR_PRIMARY,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': { background: '#026b7d' },
                    }}
                  >
                    Calculate
                  </Button>
                </Box>
              )}
              {hasPurchasePrice && !showTenYearCalculateGate && (
                <InvestmentMetricsHighlightBox
                  id="investment-metrics-highlight-ten-year"
                  heroes={[
                    {
                      key: 'ten-year-return',
                      label: '10 year return rate',
                      tooltip: annualReturnTooltip,
                      emphasis: 'primary',
                      valueVariant: 'hero',
                      value:
                        hasCashInvested && annualReturnVal != null ? (
                          formatPercentMetric(annualReturnVal)
                        ) : (
                          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#6b7280', mt: 0.5 }}>
                            Add purchase price and cash invested to see return rate
                          </Typography>
                        ),
                      footnote:
                        'Annualized: (total return over 10 years ÷ 10). Total return includes 10 years of net profit and projected resale value.',
                    },
                  ]}
                  breakdown={{
                    title: '10-year return breakdown',
                    description: `Uses estimated revenue from your report, your operating expenses and mortgage, ${TEN_YR} years of net profit, purchase price, and ${appreciationPctTen}%/yr appreciation to projected resale in year ${TEN_YR}.`,
                    rows: [
                      {
                        rowKey: 'ty-br-rev',
                        label: (
                          <>
                            Estimated revenue (LTM){' '}
                            <Typography component="span" sx={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>
                              /yr (from report)
                            </Typography>
                          </>
                        ),
                        value: fmtPrice(revenueVal),
                        valueDetail: revenueOwnerOccupiedDetail,
                        tooltip: getTooltipForMetricKey(metricsState, 'ltm_revenue'),
                      },
                      {
                        rowKey: 'ty-br-opex',
                        label: (<>− Operating expenses /yr <OperatingExpensesAdjustLink /></>),
                        value: opexVal != null ? `− ${stripLeadingMinus(fmtPrice(opexVal))}` : '—',
                        tooltip: opexTooltip,
                      },
                      {
                        rowKey: 'ty-br-noi',
                        label: (<>= Net operating income (NOI) /yr <BreakdownResultChevron /></>),
                        value: fmtPrice(noiVal),
                        tooltip: noiTooltip,
                      },
                      {
                        rowKey: 'ty-br-mortgage',
                        label: (<>− Mortgage payments /yr <LoanSetupLink /></>),
                        value: mortgageVal ? `− ${stripLeadingMinus(fmtPrice(mortgageVal))}` : '—',
                        tooltip: mortgageTooltip,
                      },
                      {
                        rowKey: 'ty-br-profit',
                        label: (<>= Net profit before tax /yr <BreakdownResultChevron /></>),
                        value: fmtPrice(profitVal),
                        tooltip: profitTooltip,
                      },
                      {
                        rowKey: 'ty-br-cum',
                        label: `Cumulative net profit (${TEN_YR} yrs, flat /yr)`,
                        value: fmtPrice(cumulativeProfitTenYr),
                        tooltip: profitTooltip,
                      },
                      {
                        rowKey: 'ty-br-sale',
                        label: (
                          <>
                            + Projected resale (year {TEN_YR}, {appreciationPctTen}%/yr appreciation)
                          </>
                        ),
                        value: propertyPriceNum > 0 ? fmtPrice(Math.round(salePriceYearTen)) : '—',
                        tooltip: annualReturnTooltip,
                      },
                      {
                        rowKey: 'ty-br-invested',
                        label: (
                          <>
                            − Total cash invested
                            {showTotalCashInvestedNudge && (
                              <>
                                {' '}
                                <LoanSetupLink />
                                <StartupCostSetupLink />
                              </>
                            )}
                          </>
                        ),
                        value: hasCashInvested ? `− ${stripLeadingMinus(fmtPrice(tciTen))}` : '—',
                        tooltip: cashInvestedTooltip,
                      },
                      {
                        rowKey: 'ty-br-gain',
                        label: (<>= Total gain if sold at year {TEN_YR} <BreakdownResultChevron /></>),
                        value: totalGainTenYr != null ? fmtPrice(totalGainTenYr) : '—',
                        tooltip: annualReturnTooltip,
                        emphasize: true,
                      },
                      {
                        rowKey: 'ty-br-pct',
                        label: `Total return on cash invested (${TEN_YR} yrs)`,
                        value:
                          totalReturnPctTenYr != null ? formatPercentMetric(totalReturnPctTenYr) : '—',
                        tooltip: annualReturnTooltip,
                      },
                      {
                        rowKey: 'ty-br-annualized',
                        label: (<>= Annualized return (total ÷ {TEN_YR}) <BreakdownResultChevron /></>),
                        value:
                          hasCashInvested && annualReturnVal != null
                            ? formatPercentMetric(annualReturnVal)
                            : '—',
                        tooltip: annualReturnTooltip,
                        emphasize: true,
                      },
                    ],
                  }}
                  customContent={
                    <CalculatorGraphSection
                      kpiData={effectiveKpiData}
                      reportType={reportType}
                      currency={currency}
                      handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
                      isUserSubscribed={isUserSubscribed}
                      appreciationRate={inputEditState.property_appreciation_rate ?? 4}
                      onAppreciationChange={(val: number) => {
                        handleInputEdit('property_appreciation_rate', val);
                      }}
                    />
                  }
                  otherMetrics={{
                    items: extraMetrics.filter(
                      m => m.key !== 'annual_return_rate' && m.key !== 'ten_years_return_rate'
                    ),
                    profitKeys,
                    fmt,
                    periodLabel,
                  }}
                />
              )}
            </Box>
          );

          if (reportType === 'manage') {
            const opexExclMgmtVal = effectiveKpiData.opex_excluding_mgmt_fee;
            const mgmtFeeDollarVal = effectiveKpiData.management_fee_dollar;
            const ownerPayoutVal = effectiveKpiData.owner_payout;
            const mgmtPctRaw = parseFloat(String(inputEditState.management_fee)) || 0;
            const mgmtPctDisplay = Math.min(mgmtPctRaw, 40);

            const ownerPayoutContent = (
              <InvestmentMetricsHighlightBox
                id="investment-metrics-highlight-owner-payout"
                heroes={[
                  {
                    key: 'owner-payout',
                    label: "Owner's 1st year payout",
                    emphasis: 'primary',
                    valueVariant: 'hero',
                    value: fmt(ownerPayoutVal, '$'),
                    footnote: 'Total revenue minus all operating expenses',
                  },
                ]}
                breakdown={{
                  title: "Owner's payout breakdown",
                  description:
                    'Shows how much the property owner receives after all operating costs, with management fee shown separately.',
                  rows: [
                    {
                      rowKey: 'op-br-rev',
                      label: (
                        <>
                          Estimated revenue (LTM){' '}
                          <Typography component="span" sx={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400 }}>
                            (from report)
                          </Typography>
                        </>
                      ),
                      value: fmt(revenueVal, '$'),
                      valueDetail: revenueOwnerOccupiedDetail,
                      tooltip: getTooltipForMetricKey(metricsState, 'ltm_revenue'),
                    },
                    {
                      rowKey: 'op-br-opex-excl',
                      label: (<>− Operating expenses (excl. management fee) <OperatingExpensesAdjustLink /></>),
                      value: opexExclMgmtVal != null ? `− ${stripLeadingMinus(fmt(opexExclMgmtVal, '$'))}` : '—',
                      tooltip: opexTooltip,
                    },
                    {
                      rowKey: 'op-br-mgmt-fee',
                      label: (<>− Management fee ({mgmtPctDisplay}%) <OperatingExpensesAdjustLink label="Set management fee" /></>),
                      value: mgmtFeeDollarVal != null ? `− ${stripLeadingMinus(fmt(mgmtFeeDollarVal, '$'))}` : '—',
                    },
                    {
                      rowKey: 'op-br-payout',
                      label: (<>= Owner's payout <BreakdownResultChevron /></>),
                      value: fmt(ownerPayoutVal, '$'),
                      emphasize: true,
                    },
                  ],
                }}
                otherMetrics={{ items: extraMetrics, profitKeys, fmt, periodLabel }}
              />
            );

            return (
              <>
                {ownerPayoutContent}
                {periodSelector}
              </>
            );
          }

          return (
            <>
              {activeMetricTab === 'cap_rate' && capRateContent}
              {activeMetricTab === 'coc_return' && cocReturnContent}
              {activeMetricTab === 'ten_year_return' && tenYearReturnContent}
              {activeMetricTab !== 'ten_year_return' && periodSelector}
            </>
          );
        }

        // Rent mode
        const extraRent = allVisibleMetrics.filter(m => !RENT_PROFIT_LAYOUT_KEYS.has(m.key));
        const monthlyRentAnnual = (parseFloat(String(inputEditState.monthly_rent)) || 0) * 12;
        const showRentInlineInput = monthlyRentAnnual <= 0 || holdRentInput;
        const rentTooltip = getTooltipForMetricKey(metricsState, 'monthly_rent');
        const periodWord = tp < 1 ? 'Monthly' : tp === 1 ? 'Annual' : `${tp}-year`;

        return (
          <>
            <InvestmentMetricsHighlightBox
              id="investment-metrics-highlight-rent"
              heroes={[
                {
                  key: 'net-profit-rent',
                  label: 'Net profit before tax',
                  tooltip: profitTooltip,
                  emphasis: 'primary',
                  valueVariant: 'hero',
                  value: showRentInlineInput ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography sx={FORECAST_INLINE_INPUT_NUDGE_SX}>
                        Enter your monthly rent in the field below — this input sets rent, not net profit
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="0"
                        type="tel"
                        value={
                          inputEditState.monthly_rent === '' ||
                          inputEditState.monthly_rent === undefined ||
                          inputEditState.monthly_rent === null
                            ? ''
                            : formatCurrency(String(inputEditState.monthly_rent))
                        }
                        onChange={e => {
                          const parsed = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0;
                          handleInputEdit('monthly_rent', parsed);
                          bumpRentHold();
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {exchangeRates[currency]?.symbol || '$'}
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '200px', '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '28px', sm: '32px' },
                          fontWeight: 800,
                          color: profitVal != null ? (profitVal >= 0 ? '#059669' : '#dc2626') : COLOR_PRIMARY,
                          lineHeight: 1.1,
                        }}
                      >
                        {fmt(profitVal, '$')}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#6b7280',
                        }}
                      >
                        {periodLabel}
                      </Typography>
                    </Box>
                  ),
                  footnote:
                    monthlyRentAnnual > 0 && !holdRentInput
                      ? `Revenue − operating expenses − rent ${periodLabel} (rental arbitrage)`
                      : undefined,
                },
              ]}
              breakdown={{
                title: `${periodWord} cash flow breakdown`,
                description:
                  'Estimated revenue from your report; operating expenses and rent from inputs below.',
                rows: [
                  {
                    rowKey: 'rent-br-rev',
                    label: `Estimated revenue ${periodLabel}`,
                    value: fmt(revenueVal, '$'),
                    valueDetail: revenueOwnerOccupiedDetail,
                    tooltip: getTooltipForMetricKey(metricsState, 'ltm_revenue'),
                  },
                  {
                    rowKey: 'rent-br-opex',
                    label: (<>− Operating expenses {periodLabel} <OperatingExpensesAdjustLink /></>),
                    value: opexVal != null ? `− ${stripLeadingMinus(fmt(opexVal, '$'))}` : '—',
                    tooltip: opexTooltip,
                  },
                  {
                    rowKey: 'rent-br-ltr',
                    label: `− Long-term rent ${periodLabel}`,
                    value: monthlyRentAnnual > 0 ? `− ${stripLeadingMinus(fmt(monthlyRentAnnual, '$'))}` : '—',
                    tooltip: rentTooltip,
                  },
                  {
                    rowKey: 'rent-br-profit',
                    label: (<>= Net profit before tax {periodLabel} <BreakdownResultChevron /></>),
                    value: fmt(profitVal, '$'),
                    tooltip: profitTooltip,
                    emphasize: true,
                  },
                ],
              }}
              otherMetrics={{ items: extraRent, profitKeys, fmt, periodLabel }}
              customContent={
                <CalculatorGraphSection
                  kpiData={effectiveKpiData}
                  reportType={reportType}
                  currency={currency}
                  handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
                  isUserSubscribed={isUserSubscribed}
                  appreciationRate={inputEditState.property_appreciation_rate ?? 4}
                  onAppreciationChange={(val: number) => {
                    handleInputEdit('property_appreciation_rate', val);
                  }}
                />
              }
            />
            {periodSelector}
          </>
        );
      })()}

      {/* Input Sections - Two Columns */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
        {(() => {
          const startupFields = inputSections.startupCost?.fields || {};

          const taxesFields = inputSections.taxes?.fields || {};

          const mergedSections: Record<string, any> = {
            ...inputSections,
            buy: {
              ...inputSections.buy,
              displayName: 'Property Price & Startup Cost',
              fields: { ...inputSections.buy.fields, ...startupFields },
              canToggle: true,
              toggleKey: '_str_ready',
              toggleLabel: 'This property is short-term rental ready!',
              toggleInverted: true,
            },
            rent: {
              ...inputSections.rent,
              displayName: 'Rent & Startup Cost',
              fields: { ...inputSections.rent.fields, ...startupFields },
              canToggle: true,
              toggleKey: '_str_ready',
              toggleLabel: 'This property is short-term rental ready!',
              toggleInverted: true,
            },
            debt: {
              ...inputSections.debt,
              displayName: 'Loan & Taxes',
              fields: { ...inputSections.debt.fields, ...taxesFields },
              secondaryGroup: {
                title: 'Taxes',
                fieldKeys: ['property_tax', 'income_tax', 'depreciation'],
                suffix: '/year',
              },
            },
          };

          const visibleSections: Record<ReportTypes, string[]> = {
            buy: ['buy', 'debt', 'operatingExpenses'],
            rent: ['rent', 'operatingExpenses'],
            manage: ['operatingExpenses', 'manage'],
          };
          const allowed = visibleSections[reportType] || Object.keys(mergedSections);
          return Object.entries(mergedSections)
            .filter(([sectionKey]) => allowed.includes(sectionKey))
            .map(([sectionKey, sectionConfig]) => (
              <Box
                key={sectionKey}
                id={
                  sectionKey === 'operatingExpenses'
                    ? FORECAST_OPEX_ANCHOR_ID
                    : sectionKey === 'debt'
                      ? FORECAST_LOAN_ANCHOR_ID
                      : sectionKey === 'buy' || sectionKey === 'rent'
                        ? FORECAST_STARTUP_ANCHOR_ID
                        : undefined
                }
                sx={{ minWidth: 0 }}
              >
                <InputSection
                  sectionKey={sectionKey}
                  sectionConfig={sectionConfig}
                  stateObj={inputEditState}
                  handleValueChange={handleInputEdit}
                  reportType={reportType}
                  currency={currency}
                  baseRevenue={baseRevenue}
                />
              </Box>
            ));
        })()}
      </Box>

      {/* Auto-save status */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '8px', mb: '16px', height: '24px' }}>
        <Typography sx={{
          fontSize: '12px', color: saveStatus === 'saved' ? '#059669' : '#9ca3af',
          transition: 'color 0.3s ease',
        }}>
          {saveStatus === 'saving' && 'Saving...'}
          {saveStatus === 'saved' && 'All changes saved'}
        </Typography>
      </Box>

      <CalculatorKPIToggleModal
        isOpen={toggleListOpen}
        onClose={() => setToggleListOpen(false)}
        title="Forecast Your Income"
        groupedMetrics={groupedMetrics}
        toggleVisibility={toggleVisibility}
      />
    </Box>
  );
};

export default InlineCalculatorForm;
